class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      // TP-Adjustment: TPSR-1518: Handle bundle removal
      const cartItem = this.closest('.cart-item');
      const isMainBundleProduct = cartItem?.dataset.tpMainProductBundle === 'true';
      const bundleId = cartItem?.dataset.tpBundleId;

      if (isMainBundleProduct && bundleId) {
        cartItems.handleBundleRemoval(bundleId);
      } else if (bundleId && this.isLastTournamentBallInBundle(cartItem)) {
        cartItems.handleBundleRemoval(bundleId);
      } else {
        cartItems.updateQuantity(this.dataset.index, 0, event);
      }
      // END TP-Adjustment
    });
  }

  isLastTournamentBallInBundle(cartItem) {
    const isTournamentBallBundle = cartItem?.dataset.tpBundleType === 'tournament-ball';
    const isTournamentBall = cartItem?.dataset.tpBundleRole === 'ball';
    if (!isTournamentBallBundle || !isTournamentBall) {
      return false;
    }

    const bundleId = cartItem?.dataset.tpBundleId;
    const bundleItems = Array.from(document.querySelectorAll(`[data-tp-bundle-id="${bundleId}"][data-tp-bundle-type="tournament-ball"]`));
    const ballItems = bundleItems.filter(e => e.dataset.tpBundleRole === "ball");

    return ballItems.length <= 1;
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // TP-Adjustment (TPSR-1270): Track error messages
    this.errorMessages = new Map();

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // TP-Adjustment: Prevent page reload on Enter key in quantity inputs
    this.addEventListener('keydown', (event) => {
      if (
        event.key === 'Enter' && 
        event.target?.classList.contains('quantity__input') && 
        event.target?.dataset?.quantityVariantId
      ) {
        event.preventDefault();
        event.stopPropagation();
        // Trigger blur, which will then trigger change event
        event.target.blur();
      }
    });
    // END TP-Adjustment
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });

    // TP-Adjustment: cartValidateBasketLimits + TPSR-2017
    this.initializeCartOnLoad();
    // END TP-Adjustment
  }

  // TP-Adjustment: Fetch cart once and reuse for all on-load validations
  async fetchCartOnLoad() {
    const cartResponse = await fetch(`${routes.cart_url}.js`);
    return cartResponse.json();
  }

  // TP-Adjustment: Run all cart on-load checks with shared cart data
  async initializeCartOnLoad() {
    try {
      const cartData = await this.fetchCartOnLoad();
      this.cartValidateBasketLimits(cartData);
      await this.removeOrphanedServiceProductsOnLoad(cartData);
    } catch (e) {
      console.error(e);
    }
  }

  // TP-Adjustment: TPSR-2017: Remove orphaned service products if main product is missing
  async removeOrphanedServiceProductsOnLoad(cartDataOnLoad) {
    const getMainCartItems = () => document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    let cartDisabled = false;
    try {
      const cartData = cartDataOnLoad || await this.fetchCartOnLoad();
      const removableBundleTypes = ['test-racket', 'strung-racket'];

      const bundles = cartData.items?.reduce((acc, item) => {
        const bundleId = item.properties?.['_bundle-id'];
        if (bundleId && removableBundleTypes.includes(item.properties?.['_bundle-type'])) {
          acc[bundleId] = (acc[bundleId] || []).concat(item);
        }
        return acc;
      }, {}) || {};

      const itemsToRemove = Object.values(bundles).flatMap(bundleItems =>
        bundleItems.some(item => item.properties?.['_main-product-bundle'] === 'true') ? [] : bundleItems
      );

      if (itemsToRemove.length === 0) return;

      this.hideSwymSFLonBundleRemoval();

      getMainCartItems()?.classList.add('cart__items--disabled');
      cartDisabled = true;

      itemsToRemove.forEach(item => {
        const lineIndex = document.querySelector(`[data-key="${item.key}"]`)?.dataset.index;
        if (lineIndex) {
          [...document.querySelectorAll(`#CartItem-${lineIndex} .loading__spinner, #CartDrawer-Item-${lineIndex} .loading__spinner`)]
            .forEach(el => el.classList.remove('hidden'));
        }
      });

      const updates = Object.fromEntries(itemsToRemove.map(item => [item.key, 0]));
      const updateResponse = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates, sections: this.getSectionsToRender().map(s => s.section), sections_url: window.location.pathname }),
      });
      const parsedState = JSON.parse(await updateResponse.text());
      if (parsedState.errors) { this.updateLiveRegions(1, parsedState.errors); return; }

      const items_removed = itemsToRemove.map(item => ({
        variant_id: item.variant_id,
        quantity: item.quantity,
        presentment_price: item.presentment_price,
        url: item.url,
      }));

      this.applyParsedCartState(parsedState, {
        source: 'cart-items',
        cartData: {
          ...parsedState,
          items: parsedState.items || [],
          items_added: [],
          items_removed,
        },
      });
    } catch (e) {
      console.error('Error removing orphaned service products:', e);
    } finally {
      if (cartDisabled) getMainCartItems()?.classList.remove('cart__items--disabled');
    }
  }
  // END TP-Adjustment

  // TP-Adjustment: cartValidateBasketLimits
  async cartValidateBasketLimitsOnLoad(cartDataOnLoad) {
    try {
      const cartData = cartDataOnLoad || await this.fetchCartOnLoad();

      this.cartValidateBasketLimits(cartData);
    } catch (e) {
      console.error(e);
    }
  }
  // END TP-Adjustment

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const inputElements = this.querySelectorAll(`input[id*="Quantity-${id}-"]`);
    inputElements.forEach((inputElement) => {
      inputElement.value = inputElement.getAttribute('value');
    });
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    // TP-Adjustment: to guard against validation of non-quantity inputs and be able to use select elements for variant selection
		if (!event.target.dataset.quantityVariantId) {
			return;
		}
    // END TP-Adjustment
    
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error('Error updating cart items:', e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  applyParsedCartState(parsedState, cartUpdatePayload) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });

    this.updateCartHeaderCount(parsedState.item_count);
    this.updateCartReco();
    this.cartValidateBasketLimits(parsedState);
    this.announceMinimumOrderNote();

    publish(PUB_SUB_EVENTS.cartUpdate, cartUpdatePayload || { source: 'cart-items', cartData: parsedState });
  }

  // TP-Adjustment: TPSR-1518: Handle bundle removal
  handleBundleRemoval(bundleId) {
    this.hideSwymSFLonBundleRemoval();
    
    const bundleItems = document.querySelectorAll(`.cart-item[data-tp-bundle-id="${bundleId}"]`);

    if (bundleItems.length === 0) {
      console.warn(`No bundle items found with bundleId: ${bundleId}`);
      return;
    }

    bundleItems.forEach((item) => {
      const line = item.dataset.index || item.dataset.line;
      if (line) {
        this.enableLoading(line);
      }
    });

    // Build updates object using variant IDs for https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-updatejs
    const updates = {};
    bundleItems.forEach((item) => {
      const key = item.dataset.key;
      if (key) {
        updates[key] = 0;
      }
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.updateLiveRegions(1, parsedState.errors);
          return;
        }

        const items_removed = Array.from(bundleItems).map(item => ({
          variant_id: item.dataset.variantId,
          quantity: item.dataset.tpGtmItemQuantity,
          presentment_price: item.dataset.tpGtmItemPrice,
        }));

        this.applyParsedCartState(parsedState, {
          source: 'cart-items',
          cartData: {
            ...parsedState,
            items_added: [],
            items_removed: items_removed,
          },
        });
      })
      .catch((e) => {
        console.error('Error removing bundle from cart:', e);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        bundleItems.forEach((item) => {
          const line = item.dataset.index || item.dataset.line;
          if (line) {
            this.disableLoading(line);
          }
        });
      });
  }
  // END TP-Adjustment

  // TP-Adjustment: TPSR-1518: Hide Swym Save for later popup on bundle removal
  hideSwymSFLonBundleRemoval() {
    const swymModalContainer = document.querySelector('.swym-modals-container');
    if (swymModalContainer) {
      swymModalContainer.style.display = 'none';
    }
  }
  // END TP-Adjustment

  // TP-Adjustment: TPSR-1518: Show Swym Save for later popup on non-bundle removal
  showSwymSFLonNonBundleRemoval() {
    const swymModalContainer = document.querySelector('.swym-modals-container');
    if (swymModalContainer) {
      swymModalContainer.style.display = '';
    }
  }
  // END TP-Adjustment

  // TP-Adjustment: Generic method to update all items in a bundle to the same quantity
  // Used by both test-racket and strung-racket bundles
  updateBundleQuantity(bundleId, quantity) {
    const bundleItems = this.querySelectorAll(`[data-tp-bundle-id="${bundleId}"]`);

    if (bundleItems.length === 0) return;

    const updates = {};
    const lineIndices = [];

    bundleItems.forEach((item) => {
      const lineKey = item.dataset.key;
      const lineIndex = item.dataset.index;
      const productType = item.dataset.tpProductType;

      // TPSR-1815: Stringing service bundle with string reels, If this is a string reel, always set to 1
        if (lineKey) {
          updates[lineKey] = productType === 'String reels' ? 1 : quantity;
        }

      if (lineIndex) {
        lineIndices.push(lineIndex);
        this.enableLoading(lineIndex);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          lineIndices.forEach((lineIndex) => {
            this.updateLiveRegions(lineIndex, parsedState.errors);
          });
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        lineIndices.forEach((lineIndex) => {
          this.updateLiveRegions(lineIndex, '');
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
      })
      .catch((error) => {
        console.error('Error updating bundle quantities:', error);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        lineIndices.forEach((lineIndex) => {
          this.disableLoading(lineIndex);
        });
      });
  }
  // END TP-Adjustment

  async updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    // TP-Adjustment: TPSR-1518: Hide Swym Save for later popup on bundle item removal
    const cartItemElement = this.querySelector(`#CartItem-${line}`);

    const isBundleItem = cartItemElement?.dataset.tpBundleId;
    const bundleType = cartItemElement?.dataset.tpBundleType;

    if (isBundleItem && quantity === 0) {
      this.hideSwymSFLonBundleRemoval();
    } else {
      this.showSwymSFLonNonBundleRemoval();
    }
    // END TP-Adjustment

    // TP-Adjustment: Sync quantity for test-racket bundle items
    // The fetch below only allows to update the quantity of one line at a time,
    // so we intercept here and update all bundle items to the same quantity instead.
    if (isBundleItem && bundleType === 'test-racket' && quantity > 0) {
      // First get amount of test rackets already in cart to check against the limit. Reduce desired
      // quantity if needed. Or prevent update if limit is already reached - show an error in that case.

      const currentCart = await window.cartApi.getCart();

      const testRacketsInCartCount = currentCart.items.reduce((count, item) => {
        if (item.properties && item.properties["_bundle-type"] === 'test-racket' && item.properties["_main-product-bundle"] === 'true') {
          return count + item.quantity;
        }
        return count;
      }, 0);

      const previousLineItem = currentCart.items.find((item) => item.key === cartItemElement.dataset.key);
      const previousQuantity = previousLineItem ? previousLineItem.quantity : 0;
      const desiredQuantity = quantity;

      const quantityDelta = desiredQuantity - previousQuantity;

      const desiredTotalTestRackets = testRacketsInCartCount + quantityDelta;

      const { testRacketMaxCount } = window.DEPT.shopMetafields;
      const { cartValidationStrings } = window.DEPT;

      if (desiredTotalTestRackets > testRacketMaxCount) {
        this.updateLiveRegions(line, `${cartValidationStrings.testRacketsLimitReachedErrorTemplate.replace('[limit]', testRacketMaxCount)}`);
        if (this.submitButton) {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
        }

        this.resetQuantityInput(line);
        this.disableLoading(line);
        return;
      }

      this.updateBundleQuantity(isBundleItem, quantity);
      return;
    }
    // END TP-Adjustment

    // TP-Adjustment: Sync quantity for strung-racket bundle items
    // When the racket quantity is updated, update string and service quantities to match
    if (isBundleItem && bundleType === 'strung-racket' && quantity > 0) {
      this.updateBundleQuantity(isBundleItem, quantity);
      return;
    }
    // END TP-Adjustment

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const items = document.querySelectorAll('.cart-item');
          if (parsedState.errors) {
            // TP-Adjustment (TPSR-1270): Get number out of error message "Only 10 items were added to your cart due to availability."
            const reportedAvailableQuantity = parsedState.errors.match(/\d+\.?\d*/g)?.[0];

            // TP-Adjustment (TPSR-1978): Handle exceptions for French and Spanish
            const languageExceptionForSingleItem = parsedState.errors.includes("Un article") || parsedState.errors.includes("añadió un artículo");
            const availableQuantity = reportedAvailableQuantity ? parseInt(reportedAvailableQuantity, 10) : languageExceptionForSingleItem ? 1 : undefined;

            if (availableQuantity) {
              this.errorMessages.set(variantId, parsedState.errors);
              this.updateQuantity(line, availableQuantity, event, name, variantId);
            } else {
              this.updateLiveRegions(line, parsedState.errors);
            }

            // TP-Adjustment (TPSR-1205): Tracking for basket limit exceeded error.
            if (!window.tracking.pushToDataLayer || !window.tracking.generateEventId) {
              return;
            }

            const payload = {
              event: 'error',
              event_id: window.tracking.generateEventId(),
              error: {
                code: "422",
                message: parsedState.errors,
                type: "basket_limit_exceeded"
              }
            }

            window.tracking.pushToDataLayer(payload);
            // END TP-Adjustment
            
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          // TP-Adjustment (TPSR-1270): Get re-rendered quantity element, updated for desktop selector.
          const quantityElement =
            document.getElementById(`Quantity-${line}-desktop`) || document.getElementById(`Drawer-quantity-${line}`);

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          // TP-Adjustment (TPSR-1270): Handle quantity difference from API error message
          if (this.errorMessages.has(variantId)) {
            message = this.errorMessages.get(variantId);
            this.errorMessages.delete(variantId);
          }
          // TP-Adjustment End
          else if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          
          this.updateLiveRegions(line, message);

          // TP-Adjustment: Update cart count specifically
          this.updateCartHeaderCount(parsedState.item_count);
          // TP-Adjustment: Update cart reco specifically
          this.updateCartReco();
          // END TP-Adjustment

          // TP-Adjustment: cartValidateBasketLimits
          this.cartValidateBasketLimits(parsedState);
          this.announceMinimumOrderNote();
          // END TP-Adjustment

          // Focus management
          const lineItemElement = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItemElement && lineItemElement.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItemElement.querySelector(`[name="${name}"]`))
              : lineItemElement.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0) {
            const cartDrawerInnerElement = cartDrawerWrapper.querySelector('.drawer__inner-empty');
            const cartDrawerLinkElement = cartDrawerWrapper.querySelector('a');
            
            if (!cartDrawerWrapper || !cartDrawerInnerElement || !cartDrawerLinkElement) return;

            trapFocus(cartDrawerInnerElement, cartDrawerLinkElement);
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((err) => {
        // TP-Adjustment (TPSR-1270): Leave loading state in place if there is an error regarding inventory. Will be handled in reload
        if (this.errorMessages.has(variantId)) {
          return;
        }
        // TP-Adjustment End

        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        // TP-Adjustment (TPSR-1270): Leave loading state in place if there is an error regarding inventory. Will be handled in reload
        if (this.errorMessages.has(variantId)) {
          return;
        }
        // TP-Adjustment End

        this.disableLoading(line);
      });
  }

  // TP-Adjustment: Update cart header count specifically
  updateCartHeaderCount(itemCount) {
    const cartCountElement = document.querySelector(".js-cart-count");
    if (cartCountElement && itemCount !== undefined) {
      // Get the current text and replace only the number
      const currentText = cartCountElement.textContent;
      const newText = currentText.replace(/\d+/, itemCount);
      cartCountElement.textContent = newText;
    }
  }

  // Target the cart template's own instance
  updateCartReco() {
    const marker = document.querySelector('[data-tp-cart-reco]');
    const section = marker?.parentElement;

    // DY does not re-embed into a replaced slider, so refreshing would swap its campaign for the Liquid version
    if (!section || section.querySelector('.dy_unit')) {
      return;
    }

    fetch(`${routes.cart_url}?section_id=${marker.dataset.sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const targetElement = section.querySelector('.tp-products-reco-slider');
        const sourceElement = html.querySelector('.tp-products-reco-slider');
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        } else if (targetElement) {
          targetElement.remove();
        } else if (sourceElement) {
          marker.after(sourceElement);
        }
        document.dispatchEvent(new CustomEvent('tp:cart-reco:update'));
      })
      .catch((e) => {
        console.error(e);
      });
  }
  // END TP-Adjustment

  updateLiveRegions(line, message) {
    const escapeHtml = (text => {
      const parser = new DOMParser().parseFromString(text, "text/html");
      return parser.documentElement.textContent || "";
    });

    // TP-Adjustment: Update error elements
    const lineItemErrorMobile = document.getElementById(`Line-item-error-${line}-mobile`);
    const lineItemErrorDesktop = document.getElementById(`Line-item-error-${line}-desktop`);
    const lineItemErrorDrawer = document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemErrorMobile) lineItemErrorMobile.querySelector('.cart-item__error-text').textContent = escapeHtml(message);
    if (lineItemErrorDesktop) lineItemErrorDesktop.querySelector('.cart-item__error-text').textContent = escapeHtml(message);
    if (lineItemErrorDrawer) lineItemErrorDrawer.querySelector('.cart-item__error-text').textContent = escapeHtml(message);
    // END TP-Adjustment

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  // TP-Adjustment: cartValidateBasketLimits
  cartValidateBasketLimits(cartData) {
    if (!cartData) {
      return;
    } 

    const cartTotal = cartData.total_price / 100;

    const basketLimitThreshold = window.DEPT?.shopMetafields?.basketLimitThreshold;

    if (!basketLimitThreshold) {
      return;
    }

    const currency = window.Shopify?.currency?.active;
  
    if (!currency) {
      return;
    }

    const countryCode = window.DEPT?.customerData?.defaultAddress?.country_code;
    
    if (!countryCode) {
      return;
    }

    const result = window.DEPT.cartValidateBasketLimit(
      cartTotal,
      basketLimitThreshold,
      currency,
      countryCode
    );

    if (!result.valid) {
      const firstCartItem = document.querySelector('[id^="CartItem-1"]');

      if (firstCartItem) {
        const threshold = basketLimitThreshold[currency];
        
        const errorMessage = window.DEPT?.cartValidationStrings?.basketLimitErrorTemplate
          ?.replace('[currency]', currency)
          ?.replace('[amount]', threshold?.toLocaleString() || '0') || 'Cart limit exceeded';
        
        this.updateLiveRegions(1, errorMessage);
      }
    }
  }
  // END TP-Adjustment

  // TP-Adjustment: DC-7077 the note lives in re-rendered markup, which screen readers don't announce
  announceMinimumOrderNote() {
    const liveRegion = document.getElementById('MinimumOrder-LiveRegion');
    if (!liveRegion) return;

    const cartFooter = document.getElementById('main-cart-footer');
    const note = cartFooter?.querySelector('.tp-minimum-order-note');
    const { reachedMessage } = liveRegion.dataset;
    const previousMessage = liveRegion.textContent.trim();
    const wasBelowMinimum = previousMessage !== '' && previousMessage !== reachedMessage;
    // Screen readers don't announce an emptied region, so the note disappearing needs its own message
    const message = note
      ? note.textContent.trim()
      : wasBelowMinimum && !cartFooter?.classList.contains('is-empty')
        ? reachedMessage
        : '';
    if (previousMessage !== message) liveRegion.textContent = message;
  }
  // END TP-Adjustment

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}